import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import moment from "moment";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDBox from "components/MDBox";
import PieChart from "examples/Charts/PieChart";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import {
  DeleteAppReviewFeedbackCall,
  GetAppReviewFeedbackCall,
} from "../../redux/api/appReviewFeedback.api";

const emptySummary = {
  thumbs_up: 0,
  thumbs_down: 0,
  total: 0,
  by_platform: [],
};

function platformLabel(key) {
  const k = String(key || "").toLowerCase();
  if (k === "ios") return "iOS";
  if (k === "android") return "Android";
  return "Unknown";
}

function FeedbackMobileCard({ row, onDelete }) {
  const email = row.user?.email || row.user_id?.slice?.(0, 8) || "—";
  return (
    <Card
      variant="outlined"
      sx={{
        p: 1.5,
        borderRadius: 2,
        borderColor: "divider",
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
        >
          <Chip
            size="small"
            color={row.sentiment === "up" ? "success" : "error"}
            icon={row.sentiment === "up" ? <ThumbUpIcon /> : <ThumbDownIcon />}
            label={row.sentiment === "up" ? "Up" : "Down"}
          />
          <Typography variant="caption" color="text.secondary">
            {moment(row.created_at).format("MMM D, HH:mm")}
          </Typography>
        </Stack>

        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ wordBreak: "break-word" }}
        >
          {email}
        </Typography>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip
            size="small"
            variant="outlined"
            label={row.is_subscriber ? "Subscriber" : "Free"}
          />
          <Chip
            size="small"
            variant="outlined"
            label={row.platform || "—"}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${row.session_count ?? "—"} sessions`}
          />
        </Stack>

        <Box display="flex" justifyContent="flex-end">
          <IconButton
            size="small"
            color="error"
            aria-label="Delete feedback"
            onClick={() => onDelete(row.id)}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      </Stack>
    </Card>
  );
}

export default function AppFeedbackPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTabletDown = useMediaQuery(theme.breakpoints.down("md"));

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await GetAppReviewFeedbackCall({
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    });
    setRows(res.rows || []);
    setTotal(res.total || 0);
    setSummary(res.summary || emptySummary);
    setLoading(false);
  }, [page, rowsPerPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const onDelete = async (id) => {
    await DeleteAppReviewFeedbackCall(id);
    void load();
  };

  const upCount = summary.thumbs_up || 0;
  const downCount = summary.thumbs_down || 0;
  const allTotal = summary.total || upCount + downCount;

  const sentimentPie = useMemo(
    () => ({
      labels: ["Thumbs up", "Thumbs down"],
      datasets: {
        label: "Feedback",
        backgroundColors: ["success", "error"],
        data: [upCount, downCount],
      },
    }),
    [upCount, downCount],
  );

  const platformBar = useMemo(() => {
    const platforms = Array.isArray(summary.by_platform)
      ? summary.by_platform
      : [];
    const labels =
      platforms.length > 0
        ? platforms.map((p) => platformLabel(p.platform))
        : ["iOS", "Android"];
    const totals =
      platforms.length > 0
        ? platforms.map((p) => Number(p.total) || 0)
        : [0, 0];

    return {
      labels,
      datasets: {
        label: "Total feedback",
        data: totals,
      },
    };
  }, [summary.by_platform]);

  const platformDescription = useMemo(() => {
    const platforms = Array.isArray(summary.by_platform)
      ? summary.by_platform
      : [];
    if (!platforms.length) return "No platform breakdown yet";
    return platforms
      .map(
        (p) =>
          `${platformLabel(p.platform)}: ${p.up || 0}↑ / ${p.down || 0}↓`,
      )
      .join(isMobile ? "\n" : " · ");
  }, [summary.by_platform, isMobile]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox
        py={{ xs: 2, md: 3 }}
        px={{ xs: 1.5, sm: 2, md: 3 }}
        sx={{ maxWidth: "100%", overflowX: "hidden" }}
      >
        <Stack spacing={0.75} mb={{ xs: 2, md: 3 }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            fontWeight={800}
            sx={{ lineHeight: 1.25 }}
          >
            App review feedback
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 720 }}
          >
            Thumbs up / down from the in-app review prompt. Totals and charts
            cover all responses.
          </Typography>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          mb={2}
          flexWrap="wrap"
          useFlexGap
          sx={{
            "& .MuiChip-root": {
              maxWidth: "100%",
            },
          }}
        >
          <Chip
            icon={<ThumbUpIcon />}
            label={`Up: ${upCount}`}
            color="success"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          />
          <Chip
            icon={<ThumbDownIcon />}
            label={`Down: ${downCount}`}
            color="error"
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          />
          <Chip
            label={`Total: ${allTotal}`}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          />
        </Stack>

        <Grid container spacing={{ xs: 3, md: 2.5 }} mb={3} mt={{ xs: 1, md: 2 }}>
          <Grid item xs={12} md={5}>
            <Box sx={{ pt: { xs: 2, md: 0 } }}>
              <PieChart
                icon={{ color: "success", component: "thumb_up" }}
                title="Sentiment (all)"
                description={`${allTotal} total responses`}
                chart={sentimentPie}
                height={isMobile ? "14rem" : "18rem"}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={7}>
            <Box sx={{ pt: { xs: 2.5, md: 0 } }}>
              <ReportsBarChart
                color="info"
                title="By platform (all)"
                description={
                  <Box
                    component="span"
                    sx={{
                      whiteSpace: isMobile ? "pre-line" : "normal",
                      display: "block",
                    }}
                  >
                    {platformDescription}
                  </Box>
                }
                date="All time"
                chart={platformBar}
              />
            </Box>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          {loading ? (
            <Box py={6} display="flex" justifyContent="center">
              <CircularProgress size={32} />
            </Box>
          ) : isTabletDown ? (
            <>
              <Stack spacing={1.25} sx={{ p: { xs: 1.25, sm: 1.5 } }}>
                {rows.length === 0 ? (
                  <Typography color="text.secondary" py={2} px={1}>
                    No feedback yet.
                  </Typography>
                ) : (
                  rows.map((row) => (
                    <FeedbackMobileCard
                      key={row.id}
                      row={row}
                      onDelete={onDelete}
                    />
                  ))
                )}
              </Stack>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage={isMobile ? "Rows" : "Rows per page:"}
                sx={{
                  borderTop: 1,
                  borderColor: "divider",
                  ".MuiTablePagination-toolbar": {
                    flexWrap: "wrap",
                    justifyContent: isMobile ? "center" : "flex-end",
                    px: 1,
                    gap: 0.5,
                    minHeight: 56,
                  },
                  ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                    {
                      margin: 0,
                    },
                }}
              />
            </>
          ) : (
            <>
              <TableContainer sx={{ maxWidth: "100%" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Sentiment</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Subscriber</TableCell>
                      <TableCell>Platform</TableCell>
                      <TableCell>Sessions</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography color="text.secondary" py={2}>
                            No feedback yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>
                            {moment(row.created_at).format(
                              "MMM D, YYYY HH:mm",
                            )}
                          </TableCell>
                          <TableCell>
                            {row.sentiment === "up" ? (
                              <Chip
                                size="small"
                                color="success"
                                icon={<ThumbUpIcon />}
                                label="Up"
                              />
                            ) : (
                              <Chip
                                size="small"
                                color="error"
                                icon={<ThumbDownIcon />}
                                label="Down"
                              />
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 240,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.user?.email ||
                              row.user_id?.slice?.(0, 8) ||
                              "—"}
                          </TableCell>
                          <TableCell>
                            {row.is_subscriber ? "Yes" : "No"}
                          </TableCell>
                          <TableCell>{row.platform || "—"}</TableCell>
                          <TableCell>{row.session_count ?? "—"}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDelete(row.id)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
              />
            </>
          )}
        </Paper>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
